import type { LucideIcon } from 'lucide-react';
import {
  Tag, ShoppingCart, ShoppingBag, Store, Package, Apple, Banana, Carrot, Beef, Fish, Milk, Coffee, Wine, Beer,
  Croissant, Cake, Pizza, Sandwich, Smartphone, Laptop, Headphones, Tv, Watch, Camera, Shirt, Footprints, Glasses,
  Crown, Gem, Scissors, Heart, Sparkles, Flower2, Pill, HeartPulse, Stethoscope, Thermometer, Baby, Home, SprayCan,
  Bath, Pen, BookOpen, Paperclip, Dumbbell, Bike, Wrench, Car, Dog, Cat, Gamepad2, Puzzle, Gift, Flame, IceCream,
  CupSoda, Popcorn, Cherry, Citrus, UtensilsCrossed, Bed, Lamp, Armchair, Backpack, Key, Music, Wallet, Briefcase,
  Calculator, DollarSign, Receipt, Banknote, CreditCard, Landmark, Wallet2, Coins, PiggyBank, WalletCards,
  // ── expanded set ──
  Salad, Soup, Egg, Cookie, Candy, CakeSlice, Wheat, Nut, Leaf, LeafyGreen, GlassWater, Droplets,
  Martini, ChefHat, Refrigerator, ShoppingBasket, ScanBarcode, Donut, Lollipop,
  Tablet, Monitor, Keyboard, Mouse, Printer, Cpu, HardDrive, Usb, BatteryCharging, Plug, Cable, Router, Wifi,
  Bluetooth, Speaker, Mic, Webcam, Projector, MemoryStick, Gamepad,
  Syringe, Cross, TestTube, Microscope, Dna, Activity,
  Ribbon, PartyPopper,
  CarFront, Cog, Gauge, Fuel, BatteryFull, Disc3, Truck, Tractor, Timer,
  Pencil, Highlighter, PenTool, Ruler, FileText, Folder, Archive, StickyNote, ClipboardList,
  Hammer, Paintbrush, HardHat, Axe, Lightbulb,
  Sofa, DoorOpen, ShowerHead, Microwave, Fan, AirVent,
} from 'lucide-react';

export type PosCategoryIconEntry = {
  name: string;
  icon: LucideIcon;
  labelKey: string;
  /** Industries this icon is recommended for (empty/omitted = general). */
  industries?: string[];
};

export const POS_CATEGORY_ICONS: PosCategoryIconEntry[] = [
  // ── General / retail ──
  { name: 'tag', icon: Tag, labelKey: 'categoryIcon.tag' },
  { name: 'shopping-cart', icon: ShoppingCart, labelKey: 'categoryIcon.shoppingCart' },
  { name: 'shopping-bag', icon: ShoppingBag, labelKey: 'categoryIcon.shoppingBag' },
  { name: 'store', icon: Store, labelKey: 'categoryIcon.store' },
  { name: 'package', icon: Package, labelKey: 'categoryIcon.package' },
  { name: 'shopping-basket', icon: ShoppingBasket, labelKey: 'categoryIcon.shoppingBasket' },
  { name: 'scan-barcode', icon: ScanBarcode, labelKey: 'categoryIcon.scanBarcode' },
  { name: 'gift', icon: Gift, labelKey: 'categoryIcon.gift' },
  { name: 'wallet', icon: Wallet, labelKey: 'categoryIcon.wallet' },
  { name: 'credit-card', icon: CreditCard, labelKey: 'categoryIcon.creditCard' },
  { name: 'coins', icon: Coins, labelKey: 'categoryIcon.coins' },
  { name: 'receipt', icon: Receipt, labelKey: 'categoryIcon.receipt' },

  // ── Groceries & Supermarket / Bakery ──
  { name: 'apple', icon: Apple, labelKey: 'categoryIcon.apple', industries: ['groceries', 'bakery'] },
  { name: 'banana', icon: Banana, labelKey: 'categoryIcon.banana', industries: ['groceries'] },
  { name: 'carrot', icon: Carrot, labelKey: 'categoryIcon.carrot', industries: ['groceries'] },
  { name: 'beef', icon: Beef, labelKey: 'categoryIcon.beef', industries: ['groceries'] },
  { name: 'fish', icon: Fish, labelKey: 'categoryIcon.fish', industries: ['groceries'] },
  { name: 'milk', icon: Milk, labelKey: 'categoryIcon.milk', industries: ['groceries'] },
  { name: 'coffee', icon: Coffee, labelKey: 'categoryIcon.coffee', industries: ['groceries', 'bakery'] },
  { name: 'wine', icon: Wine, labelKey: 'categoryIcon.wine', industries: ['groceries'] },
  { name: 'beer', icon: Beer, labelKey: 'categoryIcon.beer', industries: ['groceries'] },
  { name: 'croissant', icon: Croissant, labelKey: 'categoryIcon.croissant', industries: ['groceries', 'bakery'] },
  { name: 'cake', icon: Cake, labelKey: 'categoryIcon.cake', industries: ['bakery'] },
  { name: 'pizza', icon: Pizza, labelKey: 'categoryIcon.pizza', industries: ['groceries'] },
  { name: 'sandwich', icon: Sandwich, labelKey: 'categoryIcon.sandwich', industries: ['groceries', 'bakery'] },
  { name: 'salad', icon: Salad, labelKey: 'categoryIcon.salad', industries: ['groceries'] },
  { name: 'soup', icon: Soup, labelKey: 'categoryIcon.soup', industries: ['groceries'] },
  { name: 'egg', icon: Egg, labelKey: 'categoryIcon.egg', industries: ['groceries', 'bakery'] },
  { name: 'cookie', icon: Cookie, labelKey: 'categoryIcon.cookie', industries: ['groceries', 'bakery'] },
  { name: 'candy', icon: Candy, labelKey: 'categoryIcon.candy', industries: ['groceries', 'bakery'] },
  { name: 'ice-cream', icon: IceCream, labelKey: 'categoryIcon.iceCream', industries: ['groceries', 'bakery'] },
  { name: 'cake-slice', icon: CakeSlice, labelKey: 'categoryIcon.cakeSlice', industries: ['bakery'] },
  { name: 'donut', icon: Donut, labelKey: 'categoryIcon.donut', industries: ['bakery'] },
  { name: 'lollipop', icon: Lollipop, labelKey: 'categoryIcon.lollipop', industries: ['bakery', 'groceries'] },
  { name: 'wheat', icon: Wheat, labelKey: 'categoryIcon.wheat', industries: ['groceries', 'bakery'] },
  { name: 'nut', icon: Nut, labelKey: 'categoryIcon.nut', industries: ['groceries'] },
  { name: 'leaf', icon: Leaf, labelKey: 'categoryIcon.leaf', industries: ['groceries'] },
  { name: 'leafy-green', icon: LeafyGreen, labelKey: 'categoryIcon.leafyGreen', industries: ['groceries'] },
  { name: 'cup-soda', icon: CupSoda, labelKey: 'categoryIcon.cupSoda', industries: ['groceries'] },
  { name: 'popcorn', icon: Popcorn, labelKey: 'categoryIcon.popcorn', industries: ['groceries'] },
  { name: 'cherry', icon: Cherry, labelKey: 'categoryIcon.cherry', industries: ['groceries', 'bakery'] },
  { name: 'citrus', icon: Citrus, labelKey: 'categoryIcon.citrus', industries: ['groceries'] },
  { name: 'glass-water', icon: GlassWater, labelKey: 'categoryIcon.glassWater', industries: ['groceries'] },
  { name: 'droplets', icon: Droplets, labelKey: 'categoryIcon.droplets', industries: ['groceries'] },
  { name: 'martini', icon: Martini, labelKey: 'categoryIcon.martini', industries: ['groceries'] },
  { name: 'chef-hat', icon: ChefHat, labelKey: 'categoryIcon.chefHat', industries: ['groceries', 'bakery'] },
  { name: 'refrigerator', icon: Refrigerator, labelKey: 'categoryIcon.refrigerator', industries: ['groceries', 'electronics'] },
  { name: 'utensils-crossed', icon: UtensilsCrossed, labelKey: 'categoryIcon.utensilsCrossed', industries: ['groceries', 'bakery'] },

  // ── Mobile Phone & Accessories / Electronics ──
  { name: 'smartphone', icon: Smartphone, labelKey: 'categoryIcon.smartphone', industries: ['mobile', 'electronics'] },
  { name: 'laptop', icon: Laptop, labelKey: 'categoryIcon.laptop', industries: ['mobile', 'electronics'] },
  { name: 'tablet', icon: Tablet, labelKey: 'categoryIcon.tablet', industries: ['mobile', 'electronics'] },
  { name: 'headphones', icon: Headphones, labelKey: 'categoryIcon.headphones', industries: ['mobile', 'electronics'] },
  { name: 'tv', icon: Tv, labelKey: 'categoryIcon.tv', industries: ['electronics'] },
  { name: 'watch', icon: Watch, labelKey: 'categoryIcon.watch', industries: ['mobile', 'electronics'] },
  { name: 'camera', icon: Camera, labelKey: 'categoryIcon.camera', industries: ['electronics'] },
  { name: 'monitor', icon: Monitor, labelKey: 'categoryIcon.monitor', industries: ['electronics'] },
  { name: 'keyboard', icon: Keyboard, labelKey: 'categoryIcon.keyboard', industries: ['electronics'] },
  { name: 'mouse', icon: Mouse, labelKey: 'categoryIcon.mouse', industries: ['electronics'] },
  { name: 'printer', icon: Printer, labelKey: 'categoryIcon.printer', industries: ['electronics', 'stationery'] },
  { name: 'cpu', icon: Cpu, labelKey: 'categoryIcon.cpu', industries: ['electronics'] },
  { name: 'hard-drive', icon: HardDrive, labelKey: 'categoryIcon.hardDrive', industries: ['electronics'] },
  { name: 'usb', icon: Usb, labelKey: 'categoryIcon.usb', industries: ['mobile', 'electronics'] },
  { name: 'battery-charging', icon: BatteryCharging, labelKey: 'categoryIcon.batteryCharging', industries: ['mobile', 'electronics'] },
  { name: 'plug', icon: Plug, labelKey: 'categoryIcon.plug', industries: ['electronics', 'hardware'] },
  { name: 'cable', icon: Cable, labelKey: 'categoryIcon.cable', industries: ['mobile', 'electronics'] },
  { name: 'router', icon: Router, labelKey: 'categoryIcon.router', industries: ['electronics'] },
  { name: 'wifi', icon: Wifi, labelKey: 'categoryIcon.wifi', industries: ['electronics'] },
  { name: 'bluetooth', icon: Bluetooth, labelKey: 'categoryIcon.bluetooth', industries: ['mobile', 'electronics'] },
  { name: 'speaker', icon: Speaker, labelKey: 'categoryIcon.speaker', industries: ['electronics'] },
  { name: 'mic', icon: Mic, labelKey: 'categoryIcon.mic', industries: ['electronics'] },
  { name: 'webcam', icon: Webcam, labelKey: 'categoryIcon.webcam', industries: ['electronics'] },
  { name: 'projector', icon: Projector, labelKey: 'categoryIcon.projector', industries: ['electronics'] },
  { name: 'memory-stick', icon: MemoryStick, labelKey: 'categoryIcon.memoryStick', industries: ['electronics'] },
  { name: 'gamepad', icon: Gamepad, labelKey: 'categoryIcon.gamepad', industries: ['electronics'] },
  { name: 'gamepad-2', icon: Gamepad2, labelKey: 'categoryIcon.gamepad2', industries: ['electronics'] },

  // ── Pharmacy ──
  { name: 'pill', icon: Pill, labelKey: 'categoryIcon.pill', industries: ['pharmacy'] },
  { name: 'heart-pulse', icon: HeartPulse, labelKey: 'categoryIcon.heartPulse', industries: ['pharmacy'] },
  { name: 'stethoscope', icon: Stethoscope, labelKey: 'categoryIcon.stethoscope', industries: ['pharmacy'] },
  { name: 'thermometer', icon: Thermometer, labelKey: 'categoryIcon.thermometer', industries: ['pharmacy'] },
  { name: 'syringe', icon: Syringe, labelKey: 'categoryIcon.syringe', industries: ['pharmacy'] },
  { name: 'cross', icon: Cross, labelKey: 'categoryIcon.cross', industries: ['pharmacy'] },
  { name: 'test-tube', icon: TestTube, labelKey: 'categoryIcon.testTube', industries: ['pharmacy'] },
  { name: 'microscope', icon: Microscope, labelKey: 'categoryIcon.microscope', industries: ['pharmacy'] },
  { name: 'dna', icon: Dna, labelKey: 'categoryIcon.dna', industries: ['pharmacy'] },
  { name: 'activity', icon: Activity, labelKey: 'categoryIcon.activity', industries: ['pharmacy'] },

  // ── Perfume & Gift Shop ──
  { name: 'sparkles', icon: Sparkles, labelKey: 'categoryIcon.sparkles', industries: ['perfume'] },
  { name: 'gem', icon: Gem, labelKey: 'categoryIcon.gem', industries: ['perfume'] },
  { name: 'crown', icon: Crown, labelKey: 'categoryIcon.crown', industries: ['perfume'] },
  { name: 'flower-2', icon: Flower2, labelKey: 'categoryIcon.flower2', industries: ['perfume'] },
  { name: 'spray-can', icon: SprayCan, labelKey: 'categoryIcon.sprayCan', industries: ['perfume'] },
  { name: 'heart', icon: Heart, labelKey: 'categoryIcon.heart', industries: ['perfume'] },
  { name: 'ribbon', icon: Ribbon, labelKey: 'categoryIcon.ribbon', industries: ['perfume'] },
  { name: 'party-popper', icon: PartyPopper, labelKey: 'categoryIcon.partyPopper', industries: ['perfume'] },

  // ── Clothing / Apparel ──
  { name: 'shirt', icon: Shirt, labelKey: 'categoryIcon.shirt', industries: ['apparel'] },
  { name: 'footprints', icon: Footprints, labelKey: 'categoryIcon.footprints', industries: ['apparel'] },
  { name: 'glasses', icon: Glasses, labelKey: 'categoryIcon.glasses', industries: ['apparel', 'perfume'] },
  { name: 'backpack', icon: Backpack, labelKey: 'categoryIcon.backpack', industries: ['apparel', 'stationery'] },

  // ── Auto Parts ──
  { name: 'car', icon: Car, labelKey: 'categoryIcon.car', industries: ['auto'] },
  { name: 'car-front', icon: CarFront, labelKey: 'categoryIcon.carFront', industries: ['auto'] },
  { name: 'wrench', icon: Wrench, labelKey: 'categoryIcon.wrench', industries: ['auto', 'hardware'] },
  { name: 'cog', icon: Cog, labelKey: 'categoryIcon.cog', industries: ['auto', 'hardware'] },
  { name: 'gauge', icon: Gauge, labelKey: 'categoryIcon.gauge', industries: ['auto'] },
  { name: 'fuel', icon: Fuel, labelKey: 'categoryIcon.fuel', industries: ['auto'] },
  { name: 'battery-full', icon: BatteryFull, labelKey: 'categoryIcon.batteryFull', industries: ['auto'] },
  { name: 'disc-3', icon: Disc3, labelKey: 'categoryIcon.disc3', industries: ['auto'] },
  { name: 'truck', icon: Truck, labelKey: 'categoryIcon.truck', industries: ['auto'] },
  { name: 'tractor', icon: Tractor, labelKey: 'categoryIcon.tractor', industries: ['auto'] },
  { name: 'timer', icon: Timer, labelKey: 'categoryIcon.timer', industries: ['auto'] },

  // ── Stationery / Office ──
  { name: 'pen', icon: Pen, labelKey: 'categoryIcon.pen', industries: ['stationery'] },
  { name: 'pencil', icon: Pencil, labelKey: 'categoryIcon.pencil', industries: ['stationery'] },
  { name: 'highlighter', icon: Highlighter, labelKey: 'categoryIcon.highlighter', industries: ['stationery'] },
  { name: 'pen-tool', icon: PenTool, labelKey: 'categoryIcon.penTool', industries: ['stationery'] },
  { name: 'book-open', icon: BookOpen, labelKey: 'categoryIcon.bookOpen', industries: ['stationery'] },
  { name: 'paperclip', icon: Paperclip, labelKey: 'categoryIcon.paperclip', industries: ['stationery'] },
  { name: 'scissors', icon: Scissors, labelKey: 'categoryIcon.scissors', industries: ['stationery', 'apparel'] },
  { name: 'ruler', icon: Ruler, labelKey: 'categoryIcon.ruler', industries: ['stationery', 'hardware'] },
  { name: 'calculator', icon: Calculator, labelKey: 'categoryIcon.calculator', industries: ['stationery'] },
  { name: 'file-text', icon: FileText, labelKey: 'categoryIcon.fileText', industries: ['stationery'] },
  { name: 'folder', icon: Folder, labelKey: 'categoryIcon.folder', industries: ['stationery'] },
  { name: 'archive', icon: Archive, labelKey: 'categoryIcon.archive', industries: ['stationery'] },
  { name: 'sticky-note', icon: StickyNote, labelKey: 'categoryIcon.stickyNote', industries: ['stationery'] },
  { name: 'clipboard-list', icon: ClipboardList, labelKey: 'categoryIcon.clipboardList', industries: ['stationery'] },

  // ── Hardware / Tools ──
  { name: 'hammer', icon: Hammer, labelKey: 'categoryIcon.hammer', industries: ['hardware'] },
  { name: 'paintbrush', icon: Paintbrush, labelKey: 'categoryIcon.paintbrush', industries: ['hardware'] },
  { name: 'hard-hat', icon: HardHat, labelKey: 'categoryIcon.hardHat', industries: ['hardware'] },
  { name: 'axe', icon: Axe, labelKey: 'categoryIcon.axe', industries: ['hardware'] },
  { name: 'lightbulb', icon: Lightbulb, labelKey: 'categoryIcon.lightbulb', industries: ['hardware', 'electronics'] },

  // ── Home & Furniture ──
  { name: 'home', icon: Home, labelKey: 'categoryIcon.home', industries: ['home'] },
  { name: 'bed', icon: Bed, labelKey: 'categoryIcon.bed', industries: ['home'] },
  { name: 'lamp', icon: Lamp, labelKey: 'categoryIcon.lamp', industries: ['home'] },
  { name: 'armchair', icon: Armchair, labelKey: 'categoryIcon.armchair', industries: ['home'] },
  { name: 'sofa', icon: Sofa, labelKey: 'categoryIcon.sofa', industries: ['home'] },
  { name: 'door-open', icon: DoorOpen, labelKey: 'categoryIcon.doorOpen', industries: ['home'] },
  { name: 'bath', icon: Bath, labelKey: 'categoryIcon.bath', industries: ['home'] },
  { name: 'shower-head', icon: ShowerHead, labelKey: 'categoryIcon.showerHead', industries: ['home'] },
  { name: 'microwave', icon: Microwave, labelKey: 'categoryIcon.microwave', industries: ['home', 'electronics'] },
  { name: 'fan', icon: Fan, labelKey: 'categoryIcon.fan', industries: ['home', 'electronics'] },
  { name: 'air-vent', icon: AirVent, labelKey: 'categoryIcon.airVent', industries: ['home', 'electronics'] },

  // ── Misc / other ──
  { name: 'baby', icon: Baby, labelKey: 'categoryIcon.baby' },
  { name: 'dumbbell', icon: Dumbbell, labelKey: 'categoryIcon.dumbbell' },
  { name: 'bike', icon: Bike, labelKey: 'categoryIcon.bike' },
  { name: 'dog', icon: Dog, labelKey: 'categoryIcon.dog' },
  { name: 'cat', icon: Cat, labelKey: 'categoryIcon.cat' },
  { name: 'puzzle', icon: Puzzle, labelKey: 'categoryIcon.puzzle' },
  { name: 'flame', icon: Flame, labelKey: 'categoryIcon.flame' },
  { name: 'key', icon: Key, labelKey: 'categoryIcon.key' },
  { name: 'music', icon: Music, labelKey: 'categoryIcon.music' },
  { name: 'briefcase', icon: Briefcase, labelKey: 'categoryIcon.briefcase' },
  { name: 'dollar-sign', icon: DollarSign, labelKey: 'categoryIcon.dollarSign' },
  { name: 'banknote', icon: Banknote, labelKey: 'categoryIcon.banknote' },
  { name: 'landmark', icon: Landmark, labelKey: 'categoryIcon.landmark' },
  { name: 'wallet-2', icon: Wallet2, labelKey: 'categoryIcon.wallet2' },
  { name: 'piggy-bank', icon: PiggyBank, labelKey: 'categoryIcon.piggyBank' },
  { name: 'wallet-cards', icon: WalletCards, labelKey: 'categoryIcon.walletCards' },
];

const ICON_MAP = new Map(POS_CATEGORY_ICONS.map((entry) => [entry.name, entry.icon]));

export function getPosCategoryIcon(name?: string | null): LucideIcon | undefined {
  if (!name) return undefined;
  return ICON_MAP.get(name);
}

/** Map a master-catalog industry label to the icon group key used above. */
export function industryIconGroup(industry?: string | null): string | null {
  const s = (industry || '').toLowerCase();
  if (!s) return null;
  if (s.includes('grocer') || s.includes('supermarket')) return 'groceries';
  if (s.includes('bakery') || s.includes('sweet')) return 'bakery';
  if (s.includes('mobile') || s.includes('phone')) return 'mobile';
  if (s.includes('electronic')) return 'electronics';
  if (s.includes('pharm')) return 'pharmacy';
  if (s.includes('perfume') || s.includes('gift')) return 'perfume';
  if (s.includes('auto') || s.includes('spare')) return 'auto';
  if (s.includes('station') || s.includes('office')) return 'stationery';
  if (s.includes('hardware') || s.includes('tool')) return 'hardware';
  if (s.includes('cloth') || s.includes('apparel') || s.includes('fashion')) return 'apparel';
  if (s.includes('home') || s.includes('furniture')) return 'home';
  return null;
}

/** Deterministic palette for automatic category color assignment. */
export const POS_CATEGORY_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#78716c', '#64748b',
  '#475569', '#334155',
];

export function getDefaultCategoryColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % POS_CATEGORY_COLORS.length;
  return POS_CATEGORY_COLORS[index];
}

/** White inset ring so the active category stays obvious on any color. */
export const POS_CATEGORY_SELECTED_CLASS =
  'selected ring-[3px] ring-inset ring-white shadow-md';
